import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Box,
  TextField,
  Autocomplete,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import muiTheme, { DIALOG_RADIUS, BUTTON_RADIUS } from "../../../theme/mui/styles";

const APPROVAL_STATUS_OPTIONS = [
  { value: "approved", label: "Approved" },
  { value: "pending", label: "Pending" },
  { value: "unapproved", label: "Not Approved" },
];

const ACCOUNT_TYPE_OPTIONS = [
  "Trial",
  "Onboarding",
  "Ongoing",
  "Consolidated",
  "Aggregated",
  "Demo",
].map((label) => ({ value: label, label }));

const getEmptyFilters = () => ({
  customers: [],
  accountTypes: [],
  accounts: [],
  approvalStatus: [],
  includeAccountChildren: false,
  matchJournalAccounts: false,
});

const FilterVendorsModal = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  initialFilters,
  accountOptions = [],
  loadCustomerOptions,
}) => {
  const theme = muiTheme;
  const [localFilters, setLocalFilters] = useState(getEmptyFilters());
  const [customerOpts, setCustomerOpts] = useState([]);
  const [customerInput, setCustomerInput] = useState("");
  const [customerNext, setCustomerNext] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingMoreCustomers, setLoadingMoreCustomers] = useState(false);

  const extractCursor = useCallback((nextUrl) => {
    if (!nextUrl) return null;
    try {
      const urlObj = new URL(nextUrl);
      return urlObj.searchParams.get("cursor");
    } catch {
      return null;
    }
  }, []);

  const fetchCustomerPage = useCallback(
    async ({ searchValue = "", cursor = null, append = false } = {}) => {
      if (!loadCustomerOptions) return;
      if (append) {
        setLoadingMoreCustomers(true);
      } else {
        setLoadingCustomers(true);
      }

      try {
        const result = await loadCustomerOptions(searchValue, cursor);
        const options = Array.isArray(result?.options) ? result.options : [];
        const next = typeof result?.next === "string" ? result.next : null;

        setCustomerOpts((prev) => {
          if (!append) return options;
          const seen = new Set(prev.map((opt) => opt.value));
          const merged = [...prev];
          options.forEach((opt) => {
            if (!seen.has(opt.value)) {
              seen.add(opt.value);
              merged.push(opt);
            }
          });
          return merged;
        });

        setCustomerNext(next);
      } catch (err) {
        console.error("Failed to load customer options", err);
      } finally {
        setLoadingCustomers(false);
        setLoadingMoreCustomers(false);
      }
    },
    [loadCustomerOptions]
  );

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        accounts: [...(initialFilters?.accounts || [])],
        customers: [...(initialFilters?.customers || [])],
        accountTypes: [...(initialFilters?.accountTypes || [])],
        approvalStatus: [...(initialFilters?.approvalStatus || [])],
        includeAccountChildren: Boolean(initialFilters?.includeAccountChildren),
        matchJournalAccounts: Boolean(initialFilters?.matchJournalAccounts),
      });
    }
  }, [isOpen, initialFilters]);

  const handleChange = useCallback(
    (key) => (value) => {
      const next = value || [];
      setLocalFilters((prev) => ({
        ...prev,
        [key]: next,
        ...(key === "accounts" && next.length === 0
          ? { includeAccountChildren: false, matchJournalAccounts: false }
          : {}),
      }));
    },
    []
  );

  const handleCustomerInputChange = useCallback(
    async (_, value) => {
      setCustomerInput(value || "");
      await fetchCustomerPage({ searchValue: value || "", cursor: null, append: false });
    },
    [fetchCustomerPage]
  );

  const handleCustomersOpen = useCallback(() => {
    if (!customerOpts.length && loadCustomerOptions) {
      fetchCustomerPage({ searchValue: customerInput || "", cursor: null, append: false });
    }
  }, [customerInput, customerOpts.length, fetchCustomerPage, loadCustomerOptions]);

  const handleCustomersScroll = useCallback(
    (event) => {
      const listboxNode = event.currentTarget;
      if (!listboxNode) return;
      if (loadingCustomers || loadingMoreCustomers) return;
      if (!customerNext) return;

      const nearBottom =
        listboxNode.scrollTop + listboxNode.clientHeight >=
        listboxNode.scrollHeight - 32;
      if (!nearBottom) return;

      const cursor = extractCursor(customerNext);
      if (!cursor) return;

      fetchCustomerPage({
        searchValue: customerInput || "",
        cursor,
        append: true,
      });
    },
    [
      customerInput,
      customerNext,
      extractCursor,
      fetchCustomerPage,
      loadingCustomers,
      loadingMoreCustomers,
    ]
  );

  const handleClearLocal = useCallback(() => {
    const empty = getEmptyFilters();
    setLocalFilters(empty);
  }, []);

  const handleApply = useCallback(() => {
    onApply?.({
      accounts: [...(localFilters.accounts || [])],
      customers: [...(localFilters.customers || [])],
      accountTypes: [...(localFilters.accountTypes || [])],
      approvalStatus: [...(localFilters.approvalStatus || [])],
      includeAccountChildren: Boolean(localFilters.includeAccountChildren),
      matchJournalAccounts: Boolean(localFilters.matchJournalAccounts),
    });
  }, [localFilters, onApply]);

  const selectSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#F6F8FD",
      borderRadius: 2,
      "& fieldset": { borderColor: "#E5E7EB" },
      "&:hover fieldset": { borderColor: "#D1D5DB" },
      "&.Mui-focused fieldset": { borderColor: "#536DEC" },
    },
    "& .MuiOutlinedInput-input": {
      paddingY: "12px",
      color: "#111827",
    },
    "& .MuiSvgIcon-root": { color: "#6B7280" },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: DIALOG_RADIUS, mt: 6 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 22, pb: 0.5 }}>
          Filter Vendors
        </DialogTitle>
        <DialogContent sx={{ pt: 0.5, pb: 1.5 }}>
          <Stack spacing={4} sx={{ mt: 1 }}>
            <Stack spacing={2}>
              <Typography fontWeight={700} variant="subtitle1" color="#111827">
                Customers
              </Typography>
              <FormControl fullWidth>
                <FormLabel>Specific Customers</FormLabel>
                <Autocomplete
                  multiple
                  options={customerOpts}
                  value={localFilters.customers}
                  onChange={(_, value) => {
                    const next = value || [];
                    setLocalFilters((prev) => ({
                      ...prev,
                      customers: next,
                    }));
                  }}
                  onInputChange={handleCustomerInputChange}
                  onOpen={handleCustomersOpen}
                  inputValue={customerInput}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  getOptionLabel={(opt) => opt.label || ""}
                  loading={loadingCustomers}
                  ListboxProps={{ onScroll: handleCustomersScroll }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Search and select customers"
                      size="small"
                      sx={selectSx}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel>Customer Account Types</FormLabel>
                <Autocomplete
                  multiple
                  options={ACCOUNT_TYPE_OPTIONS}
                  value={localFilters.accountTypes}
                  onChange={(_, value) => {
                    const next = value || [];
                    setLocalFilters((prev) => ({
                      ...prev,
                      accountTypes: next,
                    }));
                  }}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  getOptionLabel={(opt) => opt.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select account types"
                      size="small"
                      sx={selectSx}
                    />
                  )}
                />
              </FormControl>
            </Stack>

            <Stack spacing={2}>
              <Typography fontWeight={700} variant="subtitle1" color="#111827">
                Accounts
              </Typography>
              <FormControl fullWidth>
                <FormLabel>Specific Accounts</FormLabel>
                <Autocomplete
                  multiple
                  options={accountOptions}
                  value={localFilters.accounts}
                  onChange={(_, value) => handleChange("accounts")(value)}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  getOptionLabel={(opt) => opt.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select accounts"
                      size="small"
                      sx={selectSx}
                    />
                  )}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localFilters.includeAccountChildren}
                      onChange={(event) =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          includeAccountChildren: event.target.checked,
                        }))
                      }
                      disabled={!localFilters.accounts.length}
                      sx={{ color: "#E5E7EB" }}
                    />
                  }
                  label="Include child accounts (match prefixes)"
                  sx={{
                    mt: 1.5,
                    color: localFilters.accounts.length ? "#111827" : "#9CA3AF",
                    "& .MuiFormControlLabel-label": {
                      color: localFilters.accounts.length ? "#111827" : "#9CA3AF",
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={localFilters.matchJournalAccounts}
                      onChange={(event) =>
                        setLocalFilters((prev) => ({
                          ...prev,
                          matchJournalAccounts: event.target.checked,
                        }))
                      }
                      disabled={!localFilters.accounts.length}
                      sx={{ color: "#E5E7EB" }}
                    />
                  }
                  label="Match journal entry accounts"
                  sx={{
                    color: localFilters.accounts.length ? "#111827" : "#9CA3AF",
                    "& .MuiFormControlLabel-label": {
                      color: localFilters.accounts.length ? "#111827" : "#9CA3AF",
                    },
                  }}
                />
              </FormControl>
            </Stack>

            <Stack spacing={2}>
              <Typography fontWeight={700} variant="subtitle1" color="#111827">
                Approval Status
              </Typography>
              <FormControl fullWidth>
                <FormLabel>Show vendors with status</FormLabel>
                <Autocomplete
                  multiple
                  options={APPROVAL_STATUS_OPTIONS}
                  value={localFilters.approvalStatus}
                  onChange={(_, value) => handleChange("approvalStatus")(value)}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                  getOptionLabel={(opt) => opt.label || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Select approval states"
                      size="small"
                      sx={selectSx}
                    />
                  )}
                />
              </FormControl>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            type="button"
            onClick={handleClearLocal}
            sx={{ textTransform: "none", color: "#536DEC", fontWeight: 600 }}
          >
            Clear Filters
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            type="button"
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: `${BUTTON_RADIUS}px !important` }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            sx={{ textTransform: "none", borderRadius: `${BUTTON_RADIUS}px !important` }}
            onClick={handleApply}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default FilterVendorsModal;
